import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { showLoading, hideLoading } from './loadingSlice';
import { showAlert, hideAlert } from './alertSlice';
import UserService from "@/Service/UserService";
import { describeFirestoreError, describeUserApiError, toSerializableError } from "@/utils/userManagementErrors";

export const fetchContributors = createAsyncThunk(
  'contributors/fetchContributors',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      dispatch(hideAlert());
      const users = await UserService.getUsers();
      return users || [];
    } catch (error) {
      const message = describeFirestoreError(error, "Error fetching users.");
      dispatch(showAlert("error", message));
      console.error("Error fetching users:", error?.code, error?.message);
      return rejectWithValue({ message, code: error?.code ?? null });
    }
  }
)

export const createContributor = createAsyncThunk(
  'contributors/createContributor',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoading());
      dispatch(hideAlert());
      const data = await UserService.createUser(payload);
      dispatch(hideLoading());
      // Backend answers { user, welcomeEmailSent, warning }; tolerate the older bare-user shape.
      const user = data?.user ?? data;
      if (data?.warning) {
        // Account exists but the contributor never got the temporary password - say so loudly.
        dispatch(showAlert("warning", data.warning));
      } else {
        dispatch(showAlert("success", `User ${user?.name || user?.email || ""} created successfully!`));
      }
      return user;
    } catch (error) {
      dispatch(hideLoading());
      // Backend answers 409 with "A user with the email X already exists." (and 403 for
      // callers without admin/manager role). Show that instead of a generic failure.
      const message = describeUserApiError(error, "Failed to create user.");
      dispatch(showAlert("error", message));
      console.error("Error creating user:", error?.message);
      return rejectWithValue(toSerializableError(error, message));
    }
  }
)

export const deleteContributor = createAsyncThunk(
  'contributors/deleteContributor',
  async (contributor, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoading());
      await UserService.deleteUser(contributor.id);
      dispatch(hideLoading());
      dispatch(showAlert("success", `User ${contributor.name} deleted successfully!`));
      console.log(`User with ID ${contributor.id} successfully deleted.`);
      return contributor.id;
    } catch (error) {
      dispatch(hideLoading());
      const message = describeUserApiError(error, "Error deleting user.");
      dispatch(showAlert("error", message));
      console.error("Error deleting user:", error?.message);
      return rejectWithValue(toSerializableError(error, message));
    }
  }
)

export const setContributorDisabled = createAsyncThunk(
  'contributors/setContributorDisabled',
  async ({ contributor, disabled }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoading());
      if (disabled) {
        await UserService.disableUser(contributor.id);
      } else {
        await UserService.enableUser(contributor.id);
      }
      dispatch(hideLoading());
      dispatch(showAlert(
        "success",
        `User ${contributor.name} ${disabled ? "disabled" : "enabled"} successfully!`,
      ));
      return { id: contributor.id, disabled };
    } catch (error) {
      dispatch(hideLoading());
      const message = describeUserApiError(
        error,
        `Failed to ${disabled ? "disable" : "enable"} user.`,
      );
      dispatch(showAlert("error", message));
      console.error("Error toggling user disabled flag:", error?.message);
      return rejectWithValue(toSerializableError(error, message));
    }
  }
)

export const sendPasswordReset = createAsyncThunk(
  'contributors/sendPasswordReset',
  async (contributor, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoading());
      const res = await UserService.sendPasswordReset(contributor.id);
      dispatch(hideLoading());
      dispatch(showAlert(
        "success",
        res?.data?.message || `Password reset email sent to ${contributor.email}.`,
      ));
      return contributor.id;
    } catch (error) {
      dispatch(hideLoading());
      // Backend returns a specific reason (disabled account, no email, user missing in Firebase,
      // email could not be delivered).
      const message = describeUserApiError(error, "Failed to send password reset email.");
      dispatch(showAlert("error", message));
      console.error("Error sending password reset:", error?.message);
      return rejectWithValue(toSerializableError(error, message));
    }
  }
)

export const changeUserInfo = createAsyncThunk(
    "contributors/changeUserRole",
    async (contributor, { dispatch, rejectWithValue }) => {
      try {
        dispatch(showLoading());
        const payload = {
          name: contributor.name,
          role: contributor.role,
        };
        const res = await UserService.changeUserInfo(contributor.id, payload);
        dispatch(hideLoading());
        dispatch(showAlert("success", `User ${contributor.name} updated successfully!`));
        return res?.data ?? payload;
      } catch (error) {
        dispatch(hideLoading());
        const message = describeUserApiError(error, "Error updating user.");
        dispatch(showAlert("error", message));
        console.error("Error updating user:", error?.message);
        return rejectWithValue(toSerializableError(error, message));
      }
    }
);

const contributorsSlice = createSlice({
  name: 'contributors',
  initialState: {
    selectedContributorId: '',
    contributors: [],
    affiliate: '',
    fetch: {
      status: 'idle', // idle | loading | succeeded | failed
      error: null,
    },
    create: {
      status: 'idle', // idle | loading | succeeded | failed
      error: null,
    },
    delete: {
      status: 'idle', // idle | loading | succeeded | failed
      error: null,
    }
  },
  reducers: {
    selectContributorId: (state, action) => {
      state.selectedContributorId = action.payload;
    },
    selectAffiliate: (state, action) => {
      console.log('selectAffiliate', action.payload);
      state.affiliate = action.payload;
    }
  },
  extraReducers: (builder) =>{
    builder
      .addCase(fetchContributors.pending, (state) => {
        state.fetch.status = 'loading';
      })
      .addCase(fetchContributors.fulfilled, (state, action) => {
        state.fetch.status = 'succeeded';
        state.contributors = action.payload;
      })
      .addCase(fetchContributors.rejected, (state, action) => {
        state.fetch.status = 'failed';
        state.fetch.error = action.error.message;
      })

      .addCase(createContributor.pending, (state) => {
        state.create.status = 'loading';
      })
      .addCase(createContributor.fulfilled, (state, action) => {
        state.create.status = 'succeeded';
        console.log('PAYLOAD', action.payload);
        state.contributors = [action.payload, ...state.contributors];
      })
      .addCase(createContributor.rejected, (state, action) => {
        state.create.status = 'failed';
        state.create.error = action.payload?.message || action.error.message;
      })

      .addCase(deleteContributor.pending, (state) => {
        state.delete.status = 'loading';
      })
      .addCase(deleteContributor.fulfilled, (state, action) => {
        state.delete.status = 'succeeded';
        state.contributors = state.contributors.filter(contributor => contributor.id !== action.payload);
      })
      .addCase(deleteContributor.rejected, (state, action) => {
        state.delete.status = 'failed';
        state.delete.error = action.error.message;
      })

      .addCase(changeUserInfo.fulfilled, (state, action) => {
        const updated = action.payload || {};
        if (!updated.id) return;
        state.contributors = state.contributors.map(contributor =>
          contributor.id === updated.id
            ? {
                ...contributor,
                ...(updated.name ? { name: updated.name } : {}),
                ...(updated.role ? { role: updated.role } : {}),
              }
            : contributor
        );
      })

      .addCase(setContributorDisabled.fulfilled, (state, action) => {
        const { id, disabled } = action.payload;
        state.contributors = state.contributors.map(contributor =>
          contributor.id === id ? { ...contributor, disabled } : contributor
        );
      });
  }
});

export const { selectContributorId, selectAffiliate } = contributorsSlice.actions;

export const useContributorsState = () => useSelector(state => state.contributors);
export const useContributorsActions = () => {
  const dispatch = useDispatch();
  return useMemo(() => ({
    selectContributorId: (contributorId) => dispatch(selectContributorId(contributorId)),
    selectAffiliate: (contributorId) => dispatch(selectAffiliate(contributorId)),
    fetchContributors: () => dispatch(fetchContributors()),
    createContributor: (contributor) => dispatch(createContributor(contributor)),
    deleteContributor: (contributor) => dispatch(deleteContributor(contributor)),
    changeUserInfo: (contributor) => dispatch(changeUserInfo(contributor)),
    sendPasswordReset: (contributor) => dispatch(sendPasswordReset(contributor)),
    setContributorDisabled: (contributor, disabled) =>
      dispatch(setContributorDisabled({ contributor, disabled })),

  }), [dispatch]);
}

export default contributorsSlice.reducer;
